'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { PageSection, DividedGrid } from '@/components';
import { MONO_FONT, SERIF_FONT } from '@/theme';
import { useStaggerIn } from '@/hooks/useStaggerIn';
import { clientGroups } from '@/libs/content/clients';
import { copy } from '@/libs/content/copy';

/**
 * Grilla de nombres de clientes (diseño del prototipo): celdas 16/9, nombre
 * serif, separadores de 1px con tinte dorado. Reutilizable en la home y en
 * /experiencia.
 */
export function ClientGrid({ clients, cols = 4 }) {
  return (
    <DividedGrid cols={cols} colsXs={2} tone="navy">
      {clients.map((c) => (
        <Box
          key={c.name}
          sx={{
            aspectRatio: '16 / 9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            transition: 'background 0.2s ease',
            '&:hover': { bgcolor: 'navy.light' },
            '&:hover span': { color: 'primary.lighter' },
            '&:hover img': { opacity: 1 },
          }}
        >
          {c.logo ? (
            <Box
              component="img"
              src={c.logo}
              alt={c.name}
              loading="lazy"
              sx={{
                maxHeight: { xs: 34, md: 42 },
                maxWidth: '74%',
                objectFit: 'contain',
                opacity: 0.85,
                transition: 'opacity 0.2s ease',
              }}
            />
          ) : (
            <Box
              component="span"
              sx={{
                fontFamily: SERIF_FONT,
                fontSize: '19px',
                letterSpacing: '0.02em',
                color: '#9aa4b8',
                textAlign: 'center',
                lineHeight: 1.25,
                transition: 'color 0.2s ease',
              }}
            >
              {c.name}
            </Box>
          )}
        </Box>
      ))}
    </DividedGrid>
  );
}

function ClientGroup({ group }) {
  const cols = group.clients.length > 6 ? 4 : 3;
  return (
    <Box sx={{ mb: { xs: 5, md: 7 }, '&:last-of-type': { mb: 0 } }}>
      <Typography
        sx={{
          fontFamily: MONO_FONT,
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'primary.main',
          mb: '18px',
        }}
      >
        {group.label}
      </Typography>
      <ClientGrid clients={group.clients} cols={cols} />
    </Box>
  );
}

export default function ClientesSection({
  id = 'clientes',
  withHeader = true,
}) {
  const ref = useStaggerIn({ stagger: 0.06 });
  const { note } = copy.clientes;
  const { clientesHeading } = copy.experiencia;

  return (
    <PageSection
      id={id}
      tone="navy"
      eyebrow={withHeader ? copy.clientes.eyebrow : undefined}
      title={withHeader ? clientesHeading : undefined}
    >
      {!withHeader && (
        <Typography
          component="h2"
          sx={{
            fontFamily: SERIF_FONT,
            fontSize: 'clamp(24px, 3vw, 36px)',
            fontWeight: 500,
            color: '#f6f1e8',
            mb: { xs: 4, md: 5 },
          }}
        >
          {clientesHeading}
        </Typography>
      )}

      <Box ref={ref}>
        {clientGroups.map((g) => (
          <ClientGroup key={g.id} group={g} />
        ))}
      </Box>

      <Typography
        sx={{ fontSize: '13px', color: 'text.onDarkFaint', mt: '20px' }}
      >
        {note}
      </Typography>
    </PageSection>
  );
}
